import { CONTRACT_INITIAL_SCORE, insulationSteps, NOISE_INSULATION_COST } from './airlines';
import {
  type Building,
  BuildingType,
  type BuildingTypeId,
  buildingDef,
  canBeBigOnly,
  contactTerminal,
  countOfType,
  crewsFree,
  dollars,
  footprint,
  isRunway,
  RunwayUsage,
  towerLevel,
} from './buildings';
import {
  MAX_AIRPORT_NAME,
  MAX_PAVE_SPAN,
  NETWORKS,
  parcelPrice,
  refundOf,
  TAXI_LINE_COST,
} from './catalog';
import { MAX_COMMAND_SPAN, TICKS_PER_GAME_DAY, TICKS_PER_GAME_HOUR } from './constants';
import { serviceSpur } from './contact';
import { FACILITIES, facilityCount, withFacility } from './facilities';
import { LOAN_OPTIONS, type Loan, loanTerms, MAX_LOANS, payoffAmount, validFee } from './finance';
import { answerGrant, GrantState, signAllowed } from './grant';
import {
  type Ground,
  isRoadTunnel,
  isServiceTunnel,
  isTaxiwayRoadTunnel,
  isTaxiwayTunnel,
  NETWORK_CENTERLINE,
  NETWORK_KIND_MASK,
  NETWORK_ROAD_TUNNEL,
  NETWORK_TAXI_ROAD_TUNNEL,
  NETWORK_TOLL,
  NETWORK_TUNNEL,
  NetworkKind,
} from './ground';
import {
  findJunctionBoxes,
  JUNCTION_COST,
  JUNCTION_TOWER,
  JunctionKind,
  junctionKindOf,
  NETWORK_JUNCTION_MASK,
  NETWORK_JUNCTION_SHIFT,
} from './junctions';
import {
  CAMPAIGN_COST,
  Campaign,
  ROADSHOW_TICKS,
  SOCIAL_BUZZ,
  SOCIAL_COOLDOWN_TICKS,
} from './marketing';
import {
  type Aircraft,
  type Contract,
  contractSlots,
  emptyReport,
  endContract,
  type Flight,
  type Ledger,
  type Offer,
  type RunwayClosure,
  terminationFee,
} from './operations';
import {
  ClosureKind,
  RESURFACE_MIN_WEAR,
  RESURFACE_TICKS,
  resurfaceCost,
  worksStart,
} from './pavement';
import {
  PLOUGH_CONTRACTOR_FEE,
  ploughJob,
  type RunwaySnow,
  SNOW_DISPATCH,
  type SnowJob,
  snowDepth,
} from './snow';
import { MAX_STAFF, NIGHT_CREW_OPTIONS, requiredStaff, STAFF_ROLES, WAGE_OPTIONS } from './staff';

/** Inclusive rectangle of tiles. Corners may be given in any order. */
export interface TileArea {
  readonly x0: number;
  readonly y0: number;
  readonly x1: number;
  readonly y1: number;
}

/**
 * Player intent. Commands travel over the network and are executed by every peer at the same
 * bundle, so they describe *what the player asked for*, never the result.
 */
export type Command =
  | { readonly type: 'buyParcel'; readonly px: number; readonly py: number }
  | {
      readonly type: 'placeBuilding';
      readonly building: number;
      readonly x: number;
      readonly y: number;
      readonly rot: number;
    }
  | { readonly type: 'upgradeBuilding'; readonly id: number }
  | { readonly type: 'demolishBuilding'; readonly id: number }
  /** Move a finished building elsewhere (and/or turn it), for MOVE_COST_PERCENT of its cost. */
  | {
      readonly type: 'moveBuilding';
      readonly id: number;
      readonly x: number;
      readonly y: number;
      readonly rot: number;
    }
  | {
      readonly type: 'buildNetwork';
      readonly kind: number;
      /** Centre line from (x0, y0) to (x1, y1), axis-aligned, in tiles. */
      readonly x0: number;
      readonly y0: number;
      readonly x1: number;
      readonly y1: number;
    }
  | { readonly type: 'removeNetwork'; readonly area: TileArea }
  | { readonly type: 'acceptOffer'; readonly offerId: number }
  | { readonly type: 'declineOffer'; readonly offerId: number }
  /** Runway use: mixed, arrivals only or departures only (RunwayMode). */
  | { readonly type: 'setRunwayMode'; readonly id: number; readonly mode: number }
  /** Who may use a runway: joint, civil only or military only (RunwayUsage). */
  | { readonly type: 'setRunwayUsage'; readonly id: number; readonly usage: number }
  /** How the road junction covering tile (x, y) is run (JunctionKind). */
  | { readonly type: 'setJunction'; readonly x: number; readonly y: number; readonly kind: number }
  /** Accept or decline the state incentive grant (see grant.ts). */
  | { readonly type: 'answerGrant'; readonly accept: boolean }
  /** Staff of one team (StaffRole): a head count, or automatic staffing to what is needed. */
  | {
      readonly type: 'setStaff';
      readonly role: number;
      readonly count: number;
      readonly auto: boolean;
    }
  /** Resurface a runway (see pavement.ts): at once, or tonight from 01:00. */
  | { readonly type: 'resurfaceRunway'; readonly id: number; readonly tonight: boolean }
  /** Pay policy: wages as a percent of the going rate (WAGE_OPTIONS). */
  | { readonly type: 'setWages'; readonly percent: number }
  /** Run a marketing campaign (CampaignId). */
  | { readonly type: 'runCampaign'; readonly campaign: number }
  /** Buy one more of a facility (FacilityId) for a terminal. */
  | { readonly type: 'addFacility'; readonly id: number; readonly facility: number }
  /** Night shift of one team (StaffRole): percent of it on duty at night (NIGHT_CREW_OPTIONS). */
  | { readonly type: 'setNightShift'; readonly role: number; readonly percent: number }
  /** Borrow one of the bank's loan sizes (index into LOAN_OPTIONS). */
  | { readonly type: 'takeLoan'; readonly option: number }
  /** Pay a loan off early. */
  | { readonly type: 'repayLoan'; readonly loanId: number }
  /** Landing and passenger fees, percent of the standard tariff. */
  | { readonly type: 'setFees'; readonly landing: number; readonly passenger: number }
  /** Pave a rectangle of taxiway (aprons and wide junctions), corners in any order. */
  | { readonly type: 'paveArea'; readonly area: TileArea }
  /** Paint (or remove) the yellow centre line aircraft follow, along an axis-aligned line. */
  | {
      readonly type: 'paintTaxiLine';
      readonly x0: number;
      readonly y0: number;
      readonly x1: number;
      readonly y1: number;
      readonly remove: boolean;
    }
  /** The airport's name, shown on signs, boards and in the news. */
  | { readonly type: 'renameAirport'; readonly name: string }
  /** End a contract early, paying the termination fee (terminationFee). */
  | { readonly type: 'cancelContract'; readonly contractId: number }
  /** Send the plough team to a snowed-up runway (see snow.ts). */
  | { readonly type: 'dispatchPlows'; readonly id: number }
  /** Admin mode on or off (temporary, for trying things out): money without end. */
  | { readonly type: 'setAdminMode'; readonly on: boolean }
  /** Pays the next step of the sound insulation programme: a bigger night noise quota. */
  | { readonly type: 'insulateHomes' }
  /** A toll plaza across the road at (x, y), or (on false) taken away. */
  | { readonly type: 'setToll'; readonly x: number; readonly y: number; readonly on: boolean }
  | { readonly type: 'setBigOnly'; readonly id: number; readonly on: boolean };

export type CommandType = Command['type'];

export type RejectReason =
  | 'invalidCommand'
  | 'outOfBounds'
  | 'tooLarge'
  | 'invalidLine'
  | 'nothingToDo'
  | 'parcelLocked'
  | 'alreadyOwned'
  | 'notAdjacent'
  | 'occupied'
  | 'blocked'
  | 'requiresTower'
  | 'requiresLevel'
  | 'limitReached'
  | 'noFreeCrew'
  | 'maxLevel'
  | 'busy'
  | 'unknownBuilding'
  | 'unknownOffer'
  | 'requiresGrant'
  | 'unknownLoan'
  | 'notJunction'
  | 'underMotorway'
  | 'needsTerminal'
  | 'standsAttached'
  | 'tunnelEnds'
  | 'tunnelJunction'
  | 'roadTunnelEnds'
  | 'roadTunnelJunction'
  | 'insufficientFunds';

/** Outcome of a command: what it would change and what it would cost (negative = refund). */
export interface Plan {
  readonly ok: boolean;
  readonly reason: RejectReason | null;
  readonly cost: number;
  readonly count: number;
}

/** The world state commands read and write. Implemented by World. */
export interface CommandContext {
  readonly ground: Ground;
  readonly buildings: Building[];
  readonly money: number;
  readonly startingParcels: number;
  nextBuildingId: number;
  readonly time: number;
  readonly offers: Offer[];
  readonly contracts: Contract[];
  readonly flights: Flight[];
  readonly aircraft: Aircraft[];
  readonly today: Ledger;
  grantState: number;
  grantAmount: number;
  grantDeadline: number;
  nextOpsId: number;
  airportName: string;
  staffHired: number[];
  staffAuto: number;
  staffNight: number[];
  wagePct: number;
  readonly loans: Loan[];
  landingFeePct: number;
  paxFeePct: number;
  buzz: number;
  /** Marketing: when the next social campaign may run, and until when a roadshow lasts. */
  socialReadyAt: number;
  promoUntil: number;
  readonly runwayClosures: RunwayClosure[];
  /** Snow on the runways and plough teams out (see snow.ts). */
  readonly runwaySnow: RunwaySnow[];
  readonly snowJobs: SnowJob[];
  /** Admin mode (AdminMode bits). */
  adminMode: number;
  /** Steps of the sound insulation programme paid. */
  noiseInsulation: number;
}

/**
 * Admin mode (temporary, for trying things out): while on, money never runs out (it is kept at
 * ADMIN_MONEY) and nothing is too dear. A world it was ever on in is marked for good: the server
 * counts its saves for no achievement or leaderboard. Saved: append only.
 */
export const AdminMode = { On: 1, Used: 2 } as const;
export const ADMIN_MONEY = dollars(1_000_000_000);

/**
 * Validates a command against the current state without changing it. The UI previews use it,
 * and the simulation calls it right before executing, so both always agree.
 */
export function planCommand(ctx: CommandContext, cmd: Command): Plan {
  const result = run(ctx, cmd, false);
  if (result.reason) return { ok: false, ...result };
  if (result.cost > 0 && result.cost > ctx.money && !(ctx.adminMode & AdminMode.On)) {
    return { ok: false, reason: 'insufficientFunds', cost: result.cost, count: result.count };
  }
  return { ok: true, ...result };
}

/** Plans and, if valid, applies a command. The caller settles the returned cost. */
export function executeCommand(ctx: CommandContext, cmd: Command): Plan {
  const plan = planCommand(ctx, cmd);
  if (plan.ok) run(ctx, cmd, true);
  return plan;
}

interface RunResult {
  reason: RejectReason | null;
  cost: number;
  count: number;
}

const reject = (reason: RejectReason): RunResult => ({ reason, cost: 0, count: 0 });
const done = (cost: number, count = 1): RunResult => ({ reason: null, cost, count });

/** Single code path for planning (`apply = false`) and executing (`apply = true`). */
function run(ctx: CommandContext, cmd: Command, apply: boolean): RunResult {
  switch (cmd.type) {
    case 'buyParcel':
      return buyParcel(ctx, cmd.px, cmd.py, apply);
    case 'placeBuilding':
      return placeBuilding(ctx, cmd.building, cmd.x, cmd.y, cmd.rot, apply);
    case 'upgradeBuilding':
      return upgradeBuilding(ctx, cmd.id, apply);
    case 'demolishBuilding':
      return demolishBuilding(ctx, cmd.id, apply);
    case 'moveBuilding':
      return moveBuilding(ctx, cmd, apply);
    case 'buildNetwork':
      return buildNetwork(ctx, cmd, apply);
    case 'removeNetwork':
      return removeNetwork(ctx, cmd.area, apply);
    case 'acceptOffer':
      return answerOffer(ctx, cmd.offerId, true, apply);
    case 'declineOffer':
      return answerOffer(ctx, cmd.offerId, false, apply);
    case 'setRunwayMode':
      return setRunwayMode(ctx, cmd.id, cmd.mode, apply);
    case 'setRunwayUsage':
      return setRunwayUsage(ctx, cmd.id, cmd.usage, apply);
    case 'setBigOnly':
      return setBigOnly(ctx, cmd.id, cmd.on, apply);
    case 'setJunction':
      return setJunction(ctx, cmd.x, cmd.y, cmd.kind, apply);
    case 'insulateHomes': {
      const step = ctx.noiseInsulation;
      if (step >= NOISE_INSULATION_COST.length) return reject('maxLevel');
      if (step >= insulationSteps(towerLevel(ctx.buildings))) return reject('requiresTower');
      const cost = NOISE_INSULATION_COST[step] ?? 0;
      if (apply) ctx.noiseInsulation = step + 1;
      return done(cost);
    }
    case 'setToll':
      return setToll(ctx, cmd.x, cmd.y, cmd.on, apply);
    case 'addFacility':
      return addFacility(ctx, cmd.id, cmd.facility, apply);
    case 'resurfaceRunway':
      return resurfaceRunway(ctx, cmd.id, cmd.tonight, apply);
    case 'runCampaign':
      return runCampaign(ctx, cmd.campaign, apply);
    case 'setWages':
      if (!WAGE_OPTIONS.includes(cmd.percent)) return reject('invalidCommand');
      if (ctx.wagePct === cmd.percent) return reject('nothingToDo');
      if (apply) ctx.wagePct = cmd.percent;
      return done(0);
    case 'answerGrant':
      if (ctx.grantState !== GrantState.Offered || typeof cmd.accept !== 'boolean') {
        return reject('unknownOffer');
      }
      // A grant is negative cost: the world adds it to the budget.
      return done(-(apply ? answerGrant(ctx, cmd.accept) : cmd.accept ? ctx.grantAmount : 0));
    case 'setStaff':
      return setStaff(ctx, cmd.role, cmd.count, cmd.auto, apply);
    case 'setNightShift':
      return setNightShift(ctx, cmd.role, cmd.percent, apply);
    case 'takeLoan':
      return takeLoan(ctx, cmd.option, apply);
    case 'repayLoan':
      return repayLoan(ctx, cmd.loanId, apply);
    case 'setFees':
      return setFees(ctx, cmd.landing, cmd.passenger, apply);
    case 'paveArea':
      return paveArea(ctx, cmd.area, apply);
    case 'paintTaxiLine':
      return paintTaxiLine(ctx, cmd, apply);
    case 'renameAirport':
      return renameAirport(ctx, cmd.name, apply);
    case 'dispatchPlows':
      return dispatchPlows(ctx, cmd.id, apply);
    case 'setAdminMode': {
      if (typeof cmd.on !== 'boolean') return reject('invalidCommand');
      const next = cmd.on ? AdminMode.On | AdminMode.Used : ctx.adminMode & AdminMode.Used;
      if (next === ctx.adminMode) return reject('nothingToDo');
      if (apply) ctx.adminMode = next;
      return done(0);
    }
    case 'cancelContract': {
      const c = ctx.contracts.find((x) => x.id === cmd.contractId);
      if (!c) return reject('unknownOffer');
      const fee = terminationFee(c, ctx.time);
      if (apply) {
        endContract(ctx, c.id);
        ctx.today.penalties += fee;
      }
      return done(fee);
    }
    default:
      return reject('invalidCommand');
  }
}

// ---------------------------------------------------------------------------------------------
// Land

function buyParcel(ctx: CommandContext, px: number, py: number, apply: boolean): RunResult {
  const g = ctx.ground;
  if (!Number.isInteger(px) || !Number.isInteger(py)) return reject('invalidCommand');
  if (px < 0 || py < 0 || px >= g.parcelsX || py >= g.parcelsY) return reject('outOfBounds');
  if (g.parcelOwned(px, py)) return reject('alreadyOwned');
  const adjacent =
    g.parcelOwned(px - 1, py) ||
    g.parcelOwned(px + 1, py) ||
    g.parcelOwned(px, py - 1) ||
    g.parcelOwned(px, py + 1);
  if (!adjacent) return reject('notAdjacent');
  const price = parcelPrice(g.ownedCount() - ctx.startingParcels);
  if (apply) g.setOwned(px, py);
  return done(price);
}

// ---------------------------------------------------------------------------------------------
// Buildings

function placeBuilding(
  ctx: CommandContext,
  type: number,
  x: number,
  y: number,
  rot: number,
  apply: boolean,
): RunResult {
  const def = buildingDef(type);
  if (!def || ![x, y, rot].every(Number.isInteger) || rot < 0 || rot > 3) {
    return reject('invalidCommand');
  }
  const g = ctx.ground;
  const { w, h } = footprint(def, rot);
  if (x < 0 || y < 0 || x + w > g.width || y + h > g.height) return reject('outOfBounds');

  let locked = false;
  const motorway = g.motorwayClear();
  for (let ty = y; ty < y + h; ty++) {
    for (let tx = x; tx < x + w; tx++) {
      if (!g.tileOwned(tx, ty)) locked = true;
      else if (g.occupantAt(tx, ty) !== 0 || g.networkKindAt(tx, ty) !== 0) {
        return reject('occupied');
      } else if (motorway(tx, ty)) return reject('underMotorway');
    }
  }
  if (locked) return reject('parcelLocked');
  if (type === BuildingType.ContactStand && !againstTerminal(ctx, x, y, rot)) {
    return reject('needsTerminal');
  }
  // A contact stand comes with its service road (see contact.ts).
  const spur = type === BuildingType.ContactStand ? contactSpur(ctx, x, y, rot) : [];
  const spurCost = spur.length * (NETWORKS[NetworkKind.Service]?.cost ?? 0);

  const tower = towerLevel(ctx.buildings);
  const first = def.levels[0];
  if (!first) return reject('invalidCommand');
  if (type === BuildingType.GrantSign && !signAllowed(ctx.grantState)) {
    return reject('requiresGrant');
  }
  if (tower < first.requiresTower) return reject('requiresTower');
  if (countOfType(ctx.buildings, type) >= (def.limits[tower] ?? 0)) return reject('limitReached');
  if (crewsFree(ctx.buildings) <= 0) return reject('noFreeCrew');

  if (apply) {
    const id = ctx.nextBuildingId++;
    const ticks = first.buildHours * TICKS_PER_GAME_HOUR;
    ctx.buildings.push({
      id,
      type: type as BuildingTypeId,
      x,
      y,
      rot,
      level: 0,
      buildRemaining: ticks,
      buildTotal: ticks,
      spent: first.cost,
      mode: 0,
      flow: 0,
      facilities: 0,
      wear: 0,
      usage: 0,
      bigOnly: 0,
    });
    g.fillOccupancy(x, y, w, h, id);
    for (const t of spur) g.setNetwork(t.x, t.y, NetworkKind.Service);
  }
  return done(first.cost + spurCost);
}

/** The service road a contact stand at (x, y, rot) is placed with (none if there is no way). */
function contactSpur(ctx: CommandContext, x: number, y: number, rot: number) {
  const g = ctx.ground;
  const at = (tx: number, ty: number) => (g.inBounds(tx, ty) ? g.occupantAt(tx, ty) : 0);
  const id = contactTerminal(ctx.buildings, at, x, y, rot);
  const terminal = ctx.buildings.find((b) => b.id === id);
  return (terminal && serviceSpur(g, ctx.buildings, terminal, x, y, rot)) ?? [];
}

function upgradeBuilding(ctx: CommandContext, id: number, apply: boolean): RunResult {
  const b = ctx.buildings.find((it) => it.id === id);
  const def = b && buildingDef(b.type);
  if (!b || !def) return reject('unknownBuilding');
  if (b.buildRemaining > 0) return reject('busy');
  const next = def.levels[b.level];
  if (!next) return reject('maxLevel');
  if (towerLevel(ctx.buildings) < next.requiresTower) return reject('requiresTower');
  if (crewsFree(ctx.buildings) <= 0) return reject('noFreeCrew');
  if (apply) {
    const ticks = next.buildHours * TICKS_PER_GAME_HOUR;
    b.buildRemaining = ticks;
    b.buildTotal = ticks;
    b.spent += next.cost;
  }
  return done(next.cost);
}

/** A contact stand at (x, y, rot) would be set against a terminal (see contactTerminal). */
function againstTerminal(ctx: CommandContext, x: number, y: number, rot: number): boolean {
  const g = ctx.ground;
  const at = (tx: number, ty: number) => (g.inBounds(tx, ty) ? g.occupantAt(tx, ty) : 0);
  return contactTerminal(ctx.buildings, at, x, y, rot) !== 0;
}

/** Contact stands set against this terminal (it can be neither moved nor pulled down). */
function hasContactStands(ctx: CommandContext, terminal: Building): boolean {
  if (terminal.type !== BuildingType.Terminal) return false;
  const g = ctx.ground;
  const at = (tx: number, ty: number) => (g.inBounds(tx, ty) ? g.occupantAt(tx, ty) : 0);
  return ctx.buildings.some(
    (s) =>
      s.type === BuildingType.ContactStand &&
      contactTerminal(ctx.buildings, at, s.x, s.y, s.rot) === terminal.id,
  );
}

function demolishBuilding(ctx: CommandContext, id: number, apply: boolean): RunResult {
  const index = ctx.buildings.findIndex((it) => it.id === id);
  const b = ctx.buildings[index];
  const def = b && buildingDef(b.type);
  if (!b || !def) return reject('unknownBuilding');
  if (hasContactStands(ctx, b)) return reject('standsAttached');
  // Not while an aircraft uses it or is on its way to it (it would go on to a stand that is gone).
  if (inUse(ctx, id)) return reject('busy');
  if (apply) {
    const { w, h } = footprint(def, b.rot);
    ctx.ground.fillOccupancy(b.x, b.y, w, h, 0);
    ctx.buildings.splice(index, 1);
  }
  return done(-refundOf(b.spent));
}

/** An aircraft is at the building (a stand, a runway) or on its way there, or a flight is due. */
function inUse(ctx: CommandContext, id: number): boolean {
  return (
    ctx.aircraft.some((a) => a.standId === id || a.runwayId === id) ||
    ctx.flights.some((f) => f.finishedAt === 0 && f.standId === id)
  );
}

/** Moving a building costs this share (percent) of what has been spent on it. */
export const MOVE_COST_PERCENT = 10;

function moveBuilding(
  ctx: CommandContext,
  cmd: Extract<Command, { type: 'moveBuilding' }>,
  apply: boolean,
): RunResult {
  const { id, x, y, rot } = cmd;
  const b = ctx.buildings.find((it) => it.id === id);
  const def = b && buildingDef(b.type);
  if (!b || !def) return reject('unknownBuilding');
  if (![x, y, rot].every(Number.isInteger) || rot < 0 || rot > 3) return reject('invalidCommand');
  if (x === b.x && y === b.y && rot === b.rot) return reject('nothingToDo');
  // Not while it is being built, nor while an aircraft uses it or is on its way to it.
  if (b.buildRemaining > 0) return reject('busy');
  if (hasContactStands(ctx, b)) return reject('standsAttached');
  if (inUse(ctx, id)) return reject('busy');

  const g = ctx.ground;
  const { w, h } = footprint(def, rot);
  if (x < 0 || y < 0 || x + w > g.width || y + h > g.height) return reject('outOfBounds');
  let locked = false;
  const motorway = g.motorwayClear();
  for (let ty = y; ty < y + h; ty++) {
    for (let tx = x; tx < x + w; tx++) {
      if (!g.tileOwned(tx, ty)) locked = true;
      else {
        // Its own old place does not count.
        const occupant = g.occupantAt(tx, ty);
        if ((occupant !== 0 && occupant !== id) || g.networkKindAt(tx, ty) !== 0) {
          return reject('occupied');
        }
        if (motorway(tx, ty)) return reject('underMotorway');
      }
    }
  }
  if (locked) return reject('parcelLocked');
  if (b.type === BuildingType.ContactStand && !againstTerminal(ctx, x, y, rot)) {
    return reject('needsTerminal');
  }
  if (apply) {
    const old = footprint(def, b.rot);
    g.fillOccupancy(b.x, b.y, old.w, old.h, 0);
    b.x = x;
    b.y = y;
    b.rot = rot;
    g.fillOccupancy(x, y, w, h, id);
  }
  return done(Math.floor((b.spent * MOVE_COST_PERCENT) / 100));
}

// ---------------------------------------------------------------------------------------------
// Airline contracts

function answerOffer(
  ctx: CommandContext,
  offerId: number,
  accept: boolean,
  apply: boolean,
): RunResult {
  const index = ctx.offers.findIndex((o) => o.id === offerId);
  const offer = ctx.offers[index];
  if (!offer || offer.expiresAt <= ctx.time) return reject('unknownOffer');
  if (apply) {
    ctx.offers.splice(index, 1);
    if (accept) {
      // Contract ids share the operations id space via the offer id, which is already unique.
      ctx.contracts.push({
        id: offer.id,
        airline: offer.airline,
        cls: offer.cls,
        slots: contractSlots(offer),
        endsAt: ctx.time + offer.days * TICKS_PER_GAME_DAY,
        served: 0,
        cancelled: 0,
        revenue: 0,
        score: CONTRACT_INITIAL_SCORE,
        strikes: 0,
        report: emptyReport(),
        cargo: offer.cargo,
        event: offer.event,
        rental: offer.rental,
      });
    }
  }
  return done(0);
}

function setBigOnly(ctx: CommandContext, id: number, on: boolean, apply: boolean): RunResult {
  const b = ctx.buildings.find((it) => it.id === id);
  if (!b) return reject('unknownBuilding');
  // Large runways (the runway at its top level), stands and parking positions only.
  if (!canBeBigOnly(b) || typeof on !== 'boolean') return reject('invalidCommand');
  if ((b.bigOnly === 1) === on) return reject('nothingToDo');
  if (apply) b.bigOnly = on ? 1 : 0;
  return done(0);
}

function setRunwayUsage(ctx: CommandContext, id: number, usage: number, apply: boolean): RunResult {
  const b = ctx.buildings.find((it) => it.id === id);
  if (!b) return reject('unknownBuilding');
  if (!isRunway(b.type)) return reject('invalidCommand');
  if (
    usage !== RunwayUsage.Joint &&
    usage !== RunwayUsage.Civil &&
    usage !== RunwayUsage.Military
  ) {
    return reject('invalidCommand');
  }
  if (b.usage === usage) return reject('nothingToDo');
  if (apply) b.usage = usage;
  return done(0);
}

/**
 * Runs the junction at (x, y) another way: traffic lights, self-timing lights or an interchange
 * (or back to plain priority). Built at its kind's price; going back to a simpler one is free.
 */
function setJunction(
  ctx: CommandContext,
  x: number,
  y: number,
  kind: number,
  apply: boolean,
): RunResult {
  if (![x, y, kind].every(Number.isInteger) || kind < 0 || kind > JunctionKind.Interchange) {
    return reject('invalidCommand');
  }
  const g = ctx.ground;
  if (!g.inBounds(x, y)) return reject('outOfBounds');
  const boxes = findJunctionBoxes(g.network, g.width, g.height);
  const box = boxes[g.index(x, y)] as number;
  if (box < 0) return reject('notJunction');
  const current = junctionKindOf(g.network, boxes, box);
  if (current === kind) return reject('nothingToDo');
  if (towerLevel(ctx.buildings) < (JUNCTION_TOWER[kind] ?? 0)) return reject('requiresTower');
  const cost = kind > current ? (JUNCTION_COST[kind] ?? 0) : 0;
  if (apply) {
    for (let t = 0; t < boxes.length; t++) {
      if (boxes[t] !== box) continue;
      const tx = t % g.width;
      const ty = (t - tx) / g.width;
      const v = g.networkAt(tx, ty) & ~NETWORK_JUNCTION_MASK;
      g.setNetwork(tx, ty, v | (kind << NETWORK_JUNCTION_SHIFT));
    }
  }
  return done(cost);
}

/** A toll plaza: booths across the whole road where it is put. */
export const TOLL_COST = dollars(25_000);
/** Widest road a plaza spans (tiles). */
const TOLL_MAX_SPAN = 16;

/**
 * The tiles a toll plaza at (x, y) spans: the road's whole width there, across the way it runs
 * (the longer of its runs through the tile is its length). Null where no plaza can stand: not a
 * road, a road too wide, or no straight stretch (a junction, a side road joining, the road
 * widening or ending right there). Two roads laid side by side make one wide road, and a plaza
 * spans it all. On the raw network, so the client can show it too.
 */
export function tollSpan(
  network: Uint8Array,
  width: number,
  height: number,
  x: number,
  y: number,
): { x: number; y: number }[] | null {
  const road = (tx: number, ty: number) =>
    tx >= 0 &&
    ty >= 0 &&
    tx < width &&
    ty < height &&
    ((network[ty * width + tx] as number) & NETWORK_KIND_MASK) === NetworkKind.Road;
  if (!road(x, y)) return null;
  const run = (dx: number, dy: number) => {
    let a = 0;
    while (road(x - dx * (a + 1), y - dy * (a + 1))) a++;
    let b = 0;
    while (road(x + dx * (b + 1), y + dy * (b + 1))) b++;
    return { a, b, length: a + b + 1 };
  };
  const along = run(1, 0);
  const down = run(0, 1);
  // Across the road: the shorter run through the tile.
  const [cross, dx, dy] = along.length >= down.length ? [down, 0, 1] : [along, 1, 0];
  if (cross.length > TOLL_MAX_SPAN) return null;
  // A straight stretch: a tile either way along the road it is just as wide, edge to edge.
  for (const side of [-1, 1]) {
    for (let i = -cross.a - 1; i <= cross.b + 1; i++) {
      const inside = i >= -cross.a && i <= cross.b;
      if (road(x + dx * i + dy * side, y + dy * i + dx * side) !== inside) return null;
    }
  }
  const out: { x: number; y: number }[] = [];
  for (let i = -cross.a; i <= cross.b; i++) out.push({ x: x + dx * i, y: y + dy * i });
  return out;
}

/**
 * A plaza widens with its road: every toll tile's span (see tollSpan) tolled across, so a road
 * laid beside one carries the plaza over it too.
 */
function spreadTolls(g: Ground): void {
  for (let t = 0; t < g.network.length; t++) {
    const v = g.network[t] as number;
    if ((v & NETWORK_KIND_MASK) !== NetworkKind.Road || (v & NETWORK_TOLL) === 0) continue;
    const x = t % g.width;
    const span = tollSpan(g.network, g.width, g.height, x, (t - x) / g.width);
    for (const p of span ?? []) g.setNetwork(p.x, p.y, g.networkAt(p.x, p.y) | NETWORK_TOLL);
  }
}

function setToll(ctx: CommandContext, x: number, y: number, on: boolean, apply: boolean) {
  if (![x, y].every(Number.isInteger) || typeof on !== 'boolean') return reject('invalidCommand');
  const g = ctx.ground;
  if (!g.inBounds(x, y)) return reject('outOfBounds');
  const span = tollSpan(g.network, g.width, g.height, x, y);
  if (!span) return reject('blocked');
  const tolled = span.filter((t) => (g.networkAt(t.x, t.y) & NETWORK_TOLL) !== 0).length;
  if (on ? tolled === span.length : tolled === 0) return reject('nothingToDo');
  if (apply) {
    for (const t of span) {
      const v = g.networkAt(t.x, t.y);
      g.setNetwork(t.x, t.y, on ? v | NETWORK_TOLL : v & ~NETWORK_TOLL);
    }
  }
  return done(on ? TOLL_COST : 0);
}

function setRunwayMode(ctx: CommandContext, id: number, mode: number, apply: boolean): RunResult {
  const b = ctx.buildings.find((it) => it.id === id);
  if (!b) return reject('unknownBuilding');
  if (!isRunway(b.type) || !Number.isInteger(mode) || mode < 0 || mode > 2) {
    return reject('invalidCommand');
  }
  if (b.mode === mode) return reject('nothingToDo');
  if (apply) b.mode = mode;
  return done(0);
}

function resurfaceRunway(
  ctx: CommandContext,
  id: number,
  tonight: boolean,
  apply: boolean,
): RunResult {
  const b = ctx.buildings.find((it) => it.id === id);
  if (!b) return reject('unknownBuilding');
  if (!isRunway(b.type) || typeof tonight !== 'boolean') {
    return reject('invalidCommand');
  }
  if (b.level < 1) return reject('requiresLevel');
  const works = ctx.runwayClosures.some((c) => c.runwayId === id && c.kind === ClosureKind.Works);
  if (b.buildRemaining > 0 || works) return reject('busy');
  if (b.wear < RESURFACE_MIN_WEAR) return reject('nothingToDo');
  const cost = resurfaceCost(b.level);
  if (apply) {
    const from = worksStart(ctx.time, tonight);
    ctx.runwayClosures.push({
      runwayId: id,
      from,
      until: from + RESURFACE_TICKS,
      kind: ClosureKind.Works,
    });
    ctx.today.upkeep += cost;
  }
  return done(cost);
}

/**
 * Sends the plough team to a runway with snow on it (or closed for it): the runway closes while
 * the ploughs work it, or stays closed until they are done (see snow.ts).
 */
function dispatchPlows(ctx: CommandContext, id: number, apply: boolean): RunResult {
  const b = ctx.buildings.find((it) => it.id === id);
  if (!b) return reject('unknownBuilding');
  if (!isRunway(b.type)) return reject('invalidCommand');
  if (b.level < 1) return reject('requiresLevel');
  if (b.buildRemaining > 0 || ctx.snowJobs.some((j) => j.runwayId === id)) return reject('busy');
  const i = ctx.runwayClosures.findIndex((c) => c.runwayId === id && c.kind === ClosureKind.Snow);
  if (i < 0 && snowDepth(ctx.runwaySnow, id) < SNOW_DISPATCH) return reject('nothingToDo');
  const job = ploughJob(ctx.buildings, b, ctx.time, ctx.ground.highwayRow());
  const cost = job.baseId === 0 ? PLOUGH_CONTRACTOR_FEE : 0;
  if (apply) {
    ctx.snowJobs.push(job);
    const closed = ctx.runwayClosures[i];
    if (closed) ctx.runwayClosures[i] = { ...closed, until: job.done };
    else {
      ctx.runwayClosures.push({
        runwayId: id,
        from: job.arrive,
        until: job.done,
        kind: ClosureKind.Snow,
      });
    }
    ctx.today.handling += cost;
  }
  return done(cost);
}

function runCampaign(ctx: CommandContext, campaign: number, apply: boolean): RunResult {
  const cost = Number.isInteger(campaign) ? CAMPAIGN_COST[campaign] : undefined;
  if (cost === undefined) return reject('invalidCommand');
  if (campaign === Campaign.Social && ctx.time < ctx.socialReadyAt) return reject('busy');
  if (campaign === Campaign.Roadshow && ctx.time < ctx.promoUntil) return reject('busy');
  if (apply) {
    if (campaign === Campaign.Social) {
      ctx.buzz = Math.min(1000, ctx.buzz + SOCIAL_BUZZ);
      ctx.socialReadyAt = ctx.time + SOCIAL_COOLDOWN_TICKS;
    } else {
      ctx.promoUntil = ctx.time + ROADSHOW_TICKS;
    }
    ctx.today.marketing += cost;
  }
  return done(cost);
}

function addFacility(ctx: CommandContext, id: number, facility: number, apply: boolean): RunResult {
  const b = ctx.buildings.find((it) => it.id === id);
  if (!b) return reject('unknownBuilding');
  const def = Number.isInteger(facility) ? FACILITIES[facility] : undefined;
  if (!def || b.type !== BuildingType.Terminal) return reject('invalidCommand');
  if (b.level < def.level || b.buildRemaining > 0) return reject('requiresLevel');
  if (facilityCount(b.facilities, def.id) >= def.max) return reject('limitReached');
  if (apply) {
    b.facilities = withFacility(b.facilities, def.id);
    b.spent += def.cost;
  }
  return done(def.cost);
}

// ---------------------------------------------------------------------------------------------
// Staff and money

function setStaff(
  ctx: CommandContext,
  role: number,
  count: number,
  auto: boolean,
  apply: boolean,
): RunResult {
  if (!STAFF_ROLES.includes(role as never) || typeof auto !== 'boolean') {
    return reject('invalidCommand');
  }
  if (!Number.isInteger(count) || count < 0 || count > MAX_STAFF) return reject('invalidCommand');
  const bit = 1 << role;
  const hired = auto ? Math.min(MAX_STAFF, requiredStaff(ctx.buildings)[role] ?? 0) : count;
  const wasAuto = (ctx.staffAuto & bit) !== 0;
  if (wasAuto === auto && ctx.staffHired[role] === hired) return reject('nothingToDo');
  if (apply) {
    ctx.staffAuto = auto ? ctx.staffAuto | bit : ctx.staffAuto & ~bit;
    ctx.staffHired[role] = hired;
  }
  return done(0);
}

function setNightShift(
  ctx: CommandContext,
  role: number,
  percent: number,
  apply: boolean,
): RunResult {
  if (!STAFF_ROLES.includes(role as never) || !NIGHT_CREW_OPTIONS.includes(percent)) {
    return reject('invalidCommand');
  }
  if (ctx.staffNight[role] === percent) return reject('nothingToDo');
  if (apply) ctx.staffNight[role] = percent;
  return done(0);
}

function takeLoan(ctx: CommandContext, option: number, apply: boolean): RunResult {
  const principal = Number.isInteger(option) ? LOAN_OPTIONS[option] : undefined;
  if (principal === undefined) return reject('invalidCommand');
  if (ctx.loans.length >= MAX_LOANS) return reject('limitReached');
  if (apply) {
    const { total, payment } = loanTerms(principal);
    ctx.loans.push({ id: ctx.nextOpsId++, principal, remaining: total, payment });
  }
  // Borrowed money is negative cost: the world adds it to the budget.
  return done(-principal);
}

function repayLoan(ctx: CommandContext, loanId: number, apply: boolean): RunResult {
  const index = ctx.loans.findIndex((l) => l.id === loanId);
  const loan = ctx.loans[index];
  if (!loan) return reject('unknownLoan');
  if (apply) ctx.loans.splice(index, 1);
  return done(payoffAmount(loan));
}

function setFees(
  ctx: CommandContext,
  landing: number,
  passenger: number,
  apply: boolean,
): RunResult {
  if (!validFee(landing) || !validFee(passenger)) return reject('invalidCommand');
  if (landing === ctx.landingFeePct && passenger === ctx.paxFeePct) return reject('nothingToDo');
  if (apply) {
    ctx.landingFeePct = landing;
    ctx.paxFeePct = passenger;
  }
  return done(0);
}

function renameAirport(ctx: CommandContext, raw: string, apply: boolean): RunResult {
  if (typeof raw !== 'string') return reject('invalidCommand');
  // Printable text only, single spaces, within the length limit.
  let printable = '';
  for (const ch of raw) {
    const code = ch.charCodeAt(0);
    if (code >= 32 && code !== 127) printable += ch;
  }
  const name = printable.replace(/\s+/g, ' ').trim();
  if (name.length === 0 || name.length > MAX_AIRPORT_NAME) return reject('invalidCommand');
  if (name === ctx.airportName) return reject('nothingToDo');
  if (apply) ctx.airportName = name;
  return done(0);
}

// ---------------------------------------------------------------------------------------------
// Networks

function paveArea(ctx: CommandContext, area: TileArea, apply: boolean): RunResult {
  if (![area.x0, area.y0, area.x1, area.y1].every(Number.isInteger)) {
    return reject('invalidCommand');
  }
  const x0 = Math.min(area.x0, area.x1);
  const x1 = Math.max(area.x0, area.x1);
  const y0 = Math.min(area.y0, area.y1);
  const y1 = Math.max(area.y0, area.y1);
  if (x1 - x0 + 1 > MAX_PAVE_SPAN || y1 - y0 + 1 > MAX_PAVE_SPAN) return reject('tooLarge');
  const g = ctx.ground;
  const motorway = g.motorwayClear();
  // All or nothing, like a drawn band.
  for (let y = y0; y <= y1; y++) {
    for (let x = x0; x <= x1; x++) {
      if (!g.inBounds(x, y)) return reject('outOfBounds');
      if (!g.tileOwned(x, y)) return reject('parcelLocked');
      if (g.occupantAt(x, y) !== 0) return reject('occupied');
      if (motorway(x, y)) return reject('underMotorway');
      const kind = g.networkKindAt(x, y);
      if (kind !== 0 && kind !== NetworkKind.Taxiway) return reject('blocked');
    }
  }
  let count = 0;
  for (let y = y0; y <= y1; y++) {
    for (let x = x0; x <= x1; x++) {
      if (g.networkKindAt(x, y) === NetworkKind.Taxiway) continue;
      count++;
      if (apply) g.setNetwork(x, y, NetworkKind.Taxiway);
    }
  }
  if (count === 0) return reject('nothingToDo');
  return done(count * (NETWORKS[NetworkKind.Taxiway]?.cost ?? 0), count);
}

function paintTaxiLine(
  ctx: CommandContext,
  cmd: Extract<Command, { type: 'paintTaxiLine' }>,
  apply: boolean,
): RunResult {
  const { x0, y0, x1, y1 } = cmd;
  if (![x0, y0, x1, y1].every(Number.isInteger) || typeof cmd.remove !== 'boolean') {
    return reject('invalidCommand');
  }
  if (x0 !== x1 && y0 !== y1) return reject('invalidLine');
  if (Math.abs(x1 - x0) + 1 > MAX_COMMAND_SPAN || Math.abs(y1 - y0) + 1 > MAX_COMMAND_SPAN) {
    return reject('tooLarge');
  }
  const g = ctx.ground;
  const dx = Math.sign(x1 - x0);
  const dy = Math.sign(y1 - y0);
  const steps = Math.max(Math.abs(x1 - x0), Math.abs(y1 - y0));
  let count = 0;
  // Only taxiway tiles carry a line; the parts of the drag over anything else are skipped.
  for (let i = 0; i <= steps; i++) {
    const x = x0 + dx * i;
    const y = y0 + dy * i;
    if (!g.inBounds(x, y) || g.networkKindAt(x, y) !== NetworkKind.Taxiway) continue;
    const v = g.networkAt(x, y);
    const has = (v & NETWORK_CENTERLINE) !== 0;
    if (has !== cmd.remove) continue;
    count++;
    if (apply) g.setNetwork(x, y, cmd.remove ? v & ~NETWORK_CENTERLINE : v | NETWORK_CENTERLINE);
  }
  if (count === 0) return reject('nothingToDo');
  return done(cmd.remove ? 0 : count * TAXI_LINE_COST, count);
}

function buildNetwork(
  ctx: CommandContext,
  cmd: Extract<Command, { type: 'buildNetwork' }>,
  apply: boolean,
): RunResult {
  const net = NETWORKS[cmd.kind];
  const { x0, y0, x1, y1 } = cmd;
  if (!net || ![x0, y0, x1, y1].every(Number.isInteger)) return reject('invalidCommand');
  if (x0 !== x1 && y0 !== y1) return reject('invalidLine');
  if (Math.abs(x1 - x0) + 1 > MAX_COMMAND_SPAN || Math.abs(y1 - y0) + 1 > MAX_COMMAND_SPAN) {
    return reject('tooLarge');
  }

  // A band `width` tiles wide around the drawn line. Taxiway centre lines are painted by the
  // player (paintTaxiLine); odd-width roads would get one.
  const vertical = x0 === x1 && y0 !== y1;
  const before = Math.floor((net.width - 1) / 2);
  const centered = net.width % 2 === 1 && cmd.kind !== NetworkKind.Taxiway;
  const g = ctx.ground;
  const along0 = vertical ? Math.min(y0, y1) : Math.min(x0, x1);
  const along1 = vertical ? Math.max(y0, y1) : Math.max(x0, x1);
  const across0 = (vertical ? x0 : y0) - before;
  const tileAt = (a: number, i: number) =>
    vertical ? { x: across0 + i, y: a } : { x: a, y: across0 + i };

  // Roads may pass under the viaduct; taxiways may not (tails are taller than its clearance).
  const motorway = cmd.kind === NetworkKind.Taxiway ? g.motorwayClear() : () => false;
  // A service road drawn across a taxiway or a road goes under it, in a tunnel; it must come out
  // the far side (no tunnel mouth on the taxiway or the road itself), and under a road it keeps
  // clear of junctions (it passes under a stretch of road, where the cars only drive on).
  const tunnels = cmd.kind === NetworkKind.Service;
  const under = (kind: number) =>
    tunnels && (kind === NetworkKind.Taxiway || kind === NetworkKind.Road);
  // A road drawn across a taxiway goes under it in a tunnel too, and so does the road a taxiway
  // is drawn across: the taxiway stays on top (see roadTunnelCheck).
  const roadUnder = (kind: number) =>
    (cmd.kind === NetworkKind.Road && kind === NetworkKind.Taxiway) ||
    (cmd.kind === NetworkKind.Taxiway && kind === NetworkKind.Road);
  let boxes: Int32Array | null = null;
  let crossing = false;
  // All or nothing: a half-built band is never what the player meant.
  for (let a = along0; a <= along1; a++) {
    for (let i = 0; i < net.width; i++) {
      const { x, y } = tileAt(a, i);
      if (!g.inBounds(x, y)) return reject('outOfBounds');
      if (!g.tileOwned(x, y)) return reject('parcelLocked');
      if (g.occupantAt(x, y) !== 0) return reject('occupied');
      if (motorway(x, y)) return reject('underMotorway');
      const kind = g.networkKindAt(x, y);
      if (under(kind)) {
        // A taxiway carries one tunnel at most, and no two side by side.
        if (isTaxiwayRoadTunnel(g.networkAt(x, y))) return reject('blocked');
        if (a === along0 || a === along1) return reject('tunnelEnds');
        if (kind === NetworkKind.Road) {
          boxes ??= findJunctionBoxes(g.network, g.width, g.height);
          if ((boxes[g.index(x, y)] as number) >= 0) return reject('tunnelJunction');
        } else if (i === 0 || i === net.width - 1) {
          const o = tileAt(a, i === 0 ? -1 : net.width);
          if (g.inBounds(o.x, o.y) && isTaxiwayRoadTunnel(g.networkAt(o.x, o.y))) {
            return reject('roadTunnelJunction');
          }
        }
        continue;
      }
      if (roadUnder(kind)) {
        crossing = true;
        continue;
      }
      if (kind !== 0 && kind !== cmd.kind) return reject('blocked');
    }
  }
  if (crossing) {
    const why = roadTunnelCheck(g, cmd.kind, along0, along1, net.width, tileAt);
    if (why) return reject(why);
  }

  let count = 0;
  let cost = 0;
  for (let a = along0; a <= along1; a++) {
    for (let i = 0; i < net.width; i++) {
      const { x, y } = tileAt(a, i);
      const current = g.networkAt(x, y);
      const kind = current & NETWORK_KIND_MASK;
      const wantCenter = centered && i === before;
      if (roadUnder(kind)) {
        // The road under the taxiway: its tunnel (once). A road a taxiway is drawn over becomes
        // the taxiway, with the tunnel under it.
        if (kind === NetworkKind.Road) {
          count++;
          cost += net.cost + TUNNEL_COST;
          if (apply) g.setNetwork(x, y, NetworkKind.Taxiway | NETWORK_TAXI_ROAD_TUNNEL);
        } else if ((current & NETWORK_TAXI_ROAD_TUNNEL) === 0) {
          count++;
          cost += TUNNEL_COST;
          if (apply) g.setNetwork(x, y, current | NETWORK_TAXI_ROAD_TUNNEL);
        }
        continue;
      }
      if (under(kind)) {
        // Under the taxiway or the road: the tunnel (once).
        const bit = kind === NetworkKind.Taxiway ? NETWORK_TUNNEL : NETWORK_ROAD_TUNNEL;
        if ((current & bit) !== 0) continue;
        count++;
        cost += TUNNEL_COST;
        if (apply) g.setNetwork(x, y, current | bit);
        continue;
      }
      if (kind === cmd.kind) {
        // Already built: only add a missing centre line, free of charge.
        if (wantCenter && (current & NETWORK_CENTERLINE) === 0) {
          count++;
          if (apply) g.setNetwork(x, y, current | NETWORK_CENTERLINE);
        }
        continue;
      }
      count++;
      cost += net.cost;
      if (apply) g.setNetwork(x, y, cmd.kind | (wantCenter ? NETWORK_CENTERLINE : 0));
    }
  }

  if (count === 0) return reject('nothingToDo');
  // The airport's first road onto the motorway brings the interchange.
  if (apply && cmd.kind === NetworkKind.Road) {
    g.layInterchange();
    spreadTolls(g);
  }
  return done(cost, count);
}

/** A tunnel under a taxiway or a road (a service road's, or a road's under a taxiway), a tile. */
export const TUNNEL_COST = dollars(1_500);

/**
 * Where a road and a taxiway cross (buildNetwork, either drawn across the other), the road goes
 * under the taxiway in a tunnel, straight across it. Tiles of the drawn band run along `a`,
 * across it `i` (0 … width − 1; tileAt takes −1 and width too, the tiles beside the band). The
 * road must come out the far side, its whole width in the tunnel; the tunnel must not pass
 * under a junction, nor run beside another tunnel or a road (cars would meet down there); a
 * stretch of taxiway it crosses gets one tunnel, all new or all there already.
 */
function roadTunnelCheck(
  g: Ground,
  kind: number,
  along0: number,
  along1: number,
  width: number,
  tileAt: (a: number, i: number) => { x: number; y: number },
): RejectReason | null {
  const valueAt = (p: { x: number; y: number }) =>
    g.inBounds(p.x, p.y) ? g.networkAt(p.x, p.y) : 0;
  const kindOf = (v: number) => v & NETWORK_KIND_MASK;
  const roadOrTunnel = (v: number) => kindOf(v) === NetworkKind.Road || isTaxiwayRoadTunnel(v);
  if (kind === NetworkKind.Road) {
    // A road drawn across a taxiway: the tiles of it on the taxiway are the tunnel.
    let before = 0;
    for (let a = along0; a <= along1; a++) {
      let fresh = 0;
      let old = 0;
      for (let i = 0; i < width; i++) {
        const v = valueAt(tileAt(a, i));
        if (kindOf(v) !== NetworkKind.Taxiway) continue;
        if (isTaxiwayTunnel(v)) return 'blocked';
        if (a === along0 || a === along1) return 'roadTunnelEnds';
        if (isTaxiwayRoadTunnel(v)) old++;
        else fresh++;
        for (const side of i === 0 ? [-1] : i === width - 1 ? [width] : []) {
          const o = valueAt(tileAt(a, side));
          const k = kindOf(o);
          if (k === NetworkKind.Road || k === NetworkKind.Highway) return 'roadTunnelJunction';
          if (isTaxiwayRoadTunnel(o) || isTaxiwayTunnel(o)) return 'roadTunnelJunction';
        }
      }
      if (fresh > 0 && old > 0) return 'roadTunnelJunction';
      const state = fresh > 0 ? 1 : old > 0 ? 2 : 0;
      if (state !== 0 && before !== 0 && state !== before) return 'roadTunnelJunction';
      before = state;
    }
    return null;
  }
  // A taxiway drawn across a road: the road's tiles under it become the tunnel. The road runs
  // across the band, its whole width under it, on beyond it either side.
  const boxes = findJunctionBoxes(g.network, g.width, g.height);
  for (let a = along0; a <= along1; a++) {
    let road = false;
    for (let i = 0; i < width; i++) {
      const p = tileAt(a, i);
      const v = valueAt(p);
      if (kindOf(v) !== NetworkKind.Road) continue;
      road = true;
      if (isRoadTunnel(v) || (v & NETWORK_TOLL) !== 0) return 'blocked';
      if ((boxes[g.index(p.x, p.y)] as number) >= 0) return 'roadTunnelJunction';
      if (a === along0 && kindOf(valueAt(tileAt(a - 1, i))) === NetworkKind.Road) {
        return 'roadTunnelEnds';
      }
      if (a === along1 && kindOf(valueAt(tileAt(a + 1, i))) === NetworkKind.Road) {
        return 'roadTunnelEnds';
      }
    }
    if (!road) continue;
    for (let i = 0; i < width; i++) {
      if (!roadOrTunnel(valueAt(tileAt(a, i)))) return 'roadTunnelEnds';
    }
    if (!roadOrTunnel(valueAt(tileAt(a, -1))) || !roadOrTunnel(valueAt(tileAt(a, width)))) {
      return 'roadTunnelEnds';
    }
  }
  return null;
}

function removeNetwork(ctx: CommandContext, area: TileArea, apply: boolean): RunResult {
  if (![area.x0, area.y0, area.x1, area.y1].every(Number.isInteger)) {
    return reject('invalidCommand');
  }
  const g = ctx.ground;
  const x0 = Math.max(0, Math.min(area.x0, area.x1));
  const x1 = Math.min(g.width - 1, Math.max(area.x0, area.x1));
  const y0 = Math.max(0, Math.min(area.y0, area.y1));
  const y1 = Math.min(g.height - 1, Math.max(area.y0, area.y1));
  if (x1 - x0 + 1 > MAX_COMMAND_SPAN || y1 - y0 + 1 > MAX_COMMAND_SPAN) return reject('tooLarge');

  let count = 0;
  let refund = 0;
  for (let y = y0; y <= y1; y++) {
    for (let x = x0; x <= x1; x++) {
      const kind = g.networkKindAt(x, y);
      // The motorway belongs to the state, not the airport.
      if (kind === 0 || kind === NetworkKind.Highway) continue;
      const v = g.networkAt(x, y);
      if (isTaxiwayRoadTunnel(v)) {
        // A road's tunnel under a taxiway: the tunnel goes, the taxiway stays.
        count++;
        refund += refundOf(TUNNEL_COST);
        if (apply) g.setNetwork(x, y, v & ~NETWORK_TAXI_ROAD_TUNNEL);
        continue;
      }
      if (isServiceTunnel(v)) {
        // A tunnel under a taxiway or a road: the tunnel goes, the taxiway or road stays.
        count++;
        refund += refundOf(TUNNEL_COST);
        if (apply)
          g.setNetwork(x, y, v & ~(isRoadTunnel(v) ? NETWORK_ROAD_TUNNEL : NETWORK_TUNNEL));
        continue;
      }
      count++;
      refund += refundOf(NETWORKS[kind]?.cost ?? 0);
      if (apply) g.setNetwork(x, y, 0);
    }
  }
  if (count === 0) return reject('nothingToDo');
  return done(-refund, count);
}
